import * as Sentry from "@sentry/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { supabaseClient } from "@/lib/supabase";

const SearchSchema = z.object({
  token_hash: z.string().optional().catch(undefined),
  type: z.string().optional().catch(undefined),
});

type ConfirmationState =
  | { status: "loading" }
  | { status: "success" }
  | { status: "error"; message: string; isExpired?: boolean }
  | { status: "invalid_params" };

export const Route = createFileRoute("/confirm-email")({
  validateSearch: SearchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { token_hash, type } = Route.useSearch();
  const navigate = useNavigate();
  const [state, setState] = useState<ConfirmationState>({ status: "loading" });

  useEffect(() => {
    async function verifyEmail() {
      // Check for missing or invalid params
      if (!token_hash || type !== "email") {
        setState({ status: "invalid_params" });
        return;
      }

      try {
        const { data, error } = await supabaseClient.auth.verifyOtp({
          type: "email",
          token_hash,
        });

        if (error) {
          Sentry.addBreadcrumb({
            category: "auth",
            message: "Email confirmation failed",
            level: "error",
            data: { errorMessage: error.message },
          });

          // Check if the error is due to an expired token
          const isExpired =
            error.message.toLowerCase().includes("expired") ||
            error.message.toLowerCase().includes("invalid") ||
            error.code === "otp_expired";

          setState({
            status: "error",
            message: error.message,
            isExpired,
          });
          return;
        }

        Sentry.addBreadcrumb({
          category: "auth",
          message: "Email confirmed successfully",
          level: "info",
          data: { userId: data.user?.id, email: data.user?.email },
        });

        setState({ status: "success" });

        // Redirect after a short delay to show success message
        setTimeout(() => {
          navigate({ to: "/clientdashboard", replace: true });
        }, 1500);
      } catch (err) {
        Sentry.captureException(err);
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "An unexpected error occurred",
        });
      }
    }

    verifyEmail();
  }, [token_hash, type, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="w-full max-w-md p-8 space-y-6 bg-card rounded-lg shadow-md text-center">
        {state.status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin" />
            <h1 className="text-2xl font-semibold text-foreground">Confirming your email...</h1>
            <p className="text-muted-foreground">Please wait while we verify your email address.</p>
          </>
        )}

        {state.status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto text-chart-2" />
            <h1 className="text-2xl font-semibold text-foreground">Email Confirmed!</h1>
            <p className="text-muted-foreground">
              Your email has been successfully verified. Redirecting you to your dashboard...
            </p>
            <Link
              to="/clientdashboard"
              className="underline text-primary hover:text-primary/80 text-sm"
            >
              Click here if you are not redirected automatically.
            </Link>
          </>
        )}

        {state.status === "error" && (
          <>
            {state.isExpired ? (
              <AlertTriangle className="w-16 h-16 mx-auto text-chart-3" />
            ) : (
              <XCircle className="w-16 h-16 mx-auto text-destructive" />
            )}
            <h1 className="text-2xl font-semibold text-foreground">
              {state.isExpired ? "Link Expired" : "Confirmation Failed"}
            </h1>
            <p className="text-muted-foreground">
              {state.isExpired
                ? "This confirmation link has expired. Please request a new confirmation email."
                : state.message}
            </p>
            <div className="pt-4 space-y-3">
              <Button onClick={() => navigate({ to: "/login" })} className="w-full">
                Go to Login
              </Button>
            </div>
          </>
        )}

        {state.status === "invalid_params" && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-destructive" />
            <h1 className="text-2xl font-semibold text-foreground">Invalid Link</h1>
            <p className="text-muted-foreground">
              This confirmation link is invalid or incomplete. Please check your email and try
              clicking the link again, or request a new confirmation email.
            </p>
            <div className="pt-4 space-y-3">
              <Button onClick={() => navigate({ to: "/login" })} className="w-full">
                Go to Login
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
